/**
 * D72 — Selective Data Wipe panel (Owner-only).
 *
 * Replaces the previous one-shot FullWipeButton. The owner picks the categories
 * they want to wipe (clients, tasks, contracts, EOD reports, reviews, team
 * chat, notes, meeting notes, calendar, notifications, AI chat), reviews live
 * row counts from `/api/admin/selective-wipe/preview`, then types DELETE to
 * execute.
 *
 * Strict access: hidden + backend-gated for super_admin / workspace owner.
 *
 * Note: Contracts is owner-only at the backend (`SELECTIVE_WIPE_CATEGORIES`)
 * and never appears in the preview body for non-owners — defense in depth.
 */
import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { AlertTriangle, Loader2, ShieldAlert, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const REQUIRED_PHRASE = 'DELETE';

// Order shown in the UI. Matches backend SELECTIVE_WIPE_CATEGORIES keys.
const CATEGORY_ORDER = [
  'clients', 'tasks', 'eod_reports', 'reviews', 'team_chat',
  'my_notes', 'meeting_notes', 'calendar', 'notifications', 'ai_chat',
  'contracts',
];

export default function SelectiveWipeButton({ role }) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('idle');     // idle | loading | preview | executing | done | error
  const [preview, setPreview] = useState(null);   // { categories: {...}, total_rows, ... }
  const [selected, setSelected] = useState({});   // { categoryKey: bool }
  const [phrase, setPhrase] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Hidden for non super_admin (backend also enforces, defense-in-depth).
  if ((role || '').toLowerCase() !== 'super_admin') return null;

  const reset = () => {
    setPhase('idle');
    setPreview(null);
    setSelected({});
    setPhrase('');
    setReceipt(null);
    setErrorMsg('');
  };

  const startPreview = async () => {
    setOpen(true);
    setPhase('loading');
    setErrorMsg('');
    try {
      const r = await fetch(`${API}/api/admin/selective-wipe/preview`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      setPreview(body);
      // Start with nothing selected — owner has to opt-in per category.
      const init = {};
      Object.keys(body.categories || {}).forEach((k) => { init[k] = false; });
      setSelected(init);
      setPhase('preview');
    } catch (e) {
      setErrorMsg(e.message || 'Failed to load preview');
      setPhase('error');
    }
  };

  const toggle = (cat) => setSelected((s) => ({ ...s, [cat]: !s[cat] }));

  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
  const selectedRowsTotal = selectedKeys.reduce(
    (sum, k) => sum + (preview?.categories?.[k]?.count || 0), 0
  );
  const phraseMatches = phrase === REQUIRED_PHRASE;
  const canExecute = phraseMatches && selectedKeys.length > 0;

  const executeWipe = async () => {
    setPhase('executing');
    setErrorMsg('');
    try {
      const r = await fetch(`${API}/api/admin/selective-wipe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
        },
        body: JSON.stringify({
          categories: selectedKeys,
          confirm_phrase: phrase,
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      setReceipt(body);
      setPhase('done');
      toast.success(`Selective wipe complete — ${body.total_rows_deleted || 0} rows removed.`);
    } catch (e) {
      setErrorMsg(e.message || 'Wipe failed');
      setPhase('error');
    }
  };

  const closeAndReload = () => {
    setOpen(false);
    reset();
    setTimeout(() => window.location.reload(), 250);
  };

  // Build category list in the canonical order, putting unknown keys last.
  const orderedKeys = preview
    ? [
        ...CATEGORY_ORDER.filter((k) => preview.categories[k]),
        ...Object.keys(preview.categories).filter((k) => !CATEGORY_ORDER.includes(k)),
      ]
    : [];

  return (
    <Card
      className="bg-red-950/30 border-red-700/40 shadow-[0_0_0_1px_rgba(239,68,68,0.15),0_2px_8px_rgba(0,0,0,0.35)]"
      data-testid="selective-wipe-card"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-300">
          <ShieldAlert className="h-4 w-4" /> Danger zone — Selective data wipe
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-red-200/80 leading-relaxed">
          Permanently delete selected categories — pick exactly what you want gone (Clients,
          Tasks, Contracts, EOD Reports, Reviews, Team Chat, Notes, etc.). <strong>Users,
          workspaces, and workspace memberships are always preserved.</strong> Type{' '}
          <code className="text-[10px] bg-red-900/40 px-1 py-0.5 rounded">DELETE</code> to
          confirm. Owner-only.
        </p>
        <Button
          variant="destructive"
          size="sm"
          onClick={startPreview}
          className="bg-red-700 hover:bg-red-600 text-white"
          data-testid="selective-wipe-open-btn"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Open Selective Wipe
        </Button>
      </CardContent>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (phase === 'executing') return;
          if (!v) { setOpen(false); reset(); }
        }}
      >
        <DialogContent
          className="sm:max-w-[640px] max-h-[88vh] overflow-y-auto bg-card border-red-700/40 shadow-[0_0_0_1px_rgba(239,68,68,0.2),0_8px_30px_rgba(0,0,0,0.6)]"
          data-testid="selective-wipe-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold flex items-center gap-2 text-red-300">
              <ShieldAlert className="h-4 w-4" /> Selective data wipe
            </DialogTitle>
          </DialogHeader>

          {phase === 'loading' && (
            <div className="py-6 text-center text-[12px] text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
              Loading preview…
            </div>
          )}

          {phase === 'error' && (
            <div className="rounded-md border border-red-700/40 bg-red-900/30 p-3">
              <p className="text-[12px] text-red-300 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" /> {errorMsg}
              </p>
              <div className="mt-3 flex justify-end">
                <Button variant="outline" size="sm" onClick={() => { setOpen(false); reset(); }}>
                  Close
                </Button>
              </div>
            </div>
          )}

          {phase === 'preview' && preview && (
            <div className="space-y-3" data-testid="selective-wipe-preview">
              <div className="rounded-md border border-amber-700/40 bg-amber-950/30 p-3 text-[11px] text-amber-200 leading-relaxed">
                Workspace: <code className="bg-amber-900/40 px-1 py-0.5 rounded">{preview.workspace_id}</code>.
                Tick the categories you want to permanently wipe. <strong>Nothing</strong> is
                deleted until you type <code>DELETE</code> and click <strong>Execute</strong>.
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">
                  Categories ({orderedKeys.length})
                </p>
                <div className="rounded-md border border-border/40 divide-y divide-border/20">
                  {orderedKeys.map((k) => {
                    const cat = preview.categories[k];
                    const empty = (cat.count || 0) === 0;
                    return (
                      <label
                        key={k}
                        htmlFor={`wipe-cat-${k}`}
                        className={`flex items-center gap-3 px-3 py-2 text-[12px] cursor-pointer hover:bg-foreground/[0.03] ${empty ? 'opacity-60' : ''}`}
                        data-testid={`selective-wipe-cat-${k}`}
                      >
                        <Checkbox
                          id={`wipe-cat-${k}`}
                          checked={!!selected[k]}
                          onCheckedChange={() => toggle(k)}
                          disabled={empty}
                          data-testid={`selective-wipe-checkbox-${k}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-foreground/90">{cat.label}</div>
                          <div className="text-[10px] text-muted-foreground/60 truncate">
                            {cat.collections.join(', ')}
                          </div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`font-mono text-[12px] ${empty ? 'text-muted-foreground/50' : 'text-red-300'}`}
                            data-testid={`selective-wipe-count-${k}`}
                          >
                            {(cat.count || 0).toLocaleString()}
                          </span>
                          <div className="text-[9px] text-muted-foreground/60">rows</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-md border border-border/30 bg-foreground/[0.03] px-3 py-2 text-[11px] text-foreground/80">
                Selected: <strong>{selectedKeys.length}</strong> categor{selectedKeys.length === 1 ? 'y' : 'ies'}{' '}
                · <strong className="text-red-300">{selectedRowsTotal.toLocaleString()}</strong> total rows to delete
              </div>

              <div className="border-t border-border/30 pt-3">
                <label className="text-[11px] font-medium text-foreground/90 block mb-1.5">
                  Type <code className="bg-foreground/[0.08] px-1.5 py-0.5 rounded text-red-300">DELETE</code> to enable:
                </label>
                <Input
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder={REQUIRED_PHRASE}
                  className="font-mono"
                  autoFocus
                  data-testid="selective-wipe-phrase-input"
                />
              </div>
            </div>
          )}

          {phase === 'executing' && (
            <div className="py-8 text-center" data-testid="selective-wipe-executing">
              <Loader2 className="h-6 w-6 animate-spin inline-block text-red-400 mb-3" />
              <p className="text-[12px] text-foreground/80">Wiping selected data… don't close this tab.</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">
                Storage blob deletes can take 30+ seconds.
              </p>
            </div>
          )}

          {phase === 'done' && receipt && (
            <div className="space-y-3" data-testid="selective-wipe-receipt">
              <div className="rounded-md border border-emerald-700/40 bg-emerald-950/30 p-3 text-[11px] text-emerald-200 leading-relaxed flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-300" />
                <div>
                  Wipe complete. Removed{' '}
                  <strong className="text-emerald-100">{(receipt.total_rows_deleted || 0).toLocaleString()}</strong>{' '}
                  document{(receipt.total_rows_deleted || 0) === 1 ? '' : 's'} and{' '}
                  <strong className="text-emerald-100">{receipt.storage_blobs_deleted || 0}</strong>{' '}
                  storage blob{(receipt.storage_blobs_deleted || 0) === 1 ? '' : 's'} across{' '}
                  {receipt.categories_wiped?.length || 0} categor{(receipt.categories_wiped?.length || 0) === 1 ? 'y' : 'ies'}.
                </div>
              </div>
              {receipt.storage_blob_errors > 0 && (
                <div className="rounded-md border border-amber-700/40 bg-amber-950/30 p-3 text-[11px] text-amber-200">
                  {receipt.storage_blob_errors} storage delete error{receipt.storage_blob_errors === 1 ? '' : 's'}
                  {' '}(safe to ignore — orphan blobs).
                </div>
              )}
              <details className="rounded-md border border-border/30 bg-foreground/[0.02] p-2">
                <summary className="text-[10px] uppercase tracking-wider text-muted-foreground/70 cursor-pointer">
                  Full receipt
                </summary>
                <pre className="text-[10px] mt-2 max-h-[260px] overflow-auto whitespace-pre-wrap text-foreground/80">
                  {JSON.stringify(receipt, null, 2)}
                </pre>
              </details>
            </div>
          )}

          <DialogFooter>
            {phase === 'preview' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setOpen(false); reset(); }}
                  data-testid="selective-wipe-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={!canExecute}
                  onClick={executeWipe}
                  className="bg-red-700 hover:bg-red-600 text-white disabled:bg-red-900/40 disabled:text-red-300/40"
                  data-testid="selective-wipe-execute-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Execute wipe ({selectedRowsTotal.toLocaleString()} rows)
                </Button>
              </>
            )}
            {phase === 'done' && (
              <Button size="sm" onClick={closeAndReload} data-testid="selective-wipe-close-btn">
                Reload app
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
